import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accountMonitoringApi, type PolicyWarning } from '@/services/api/accountMonitoring';
import { cn } from '@/utils/cn';
import { formatAccountId } from '@/utils';
import { useAuthStore } from '@/store/authStore';

interface PolicyWarningBannerProps {
  className?: string;
}

export const DISMISS_STORAGE_KEY_PREFIX = 'policy-warning-dismissed:';
const DISMISS_DURATION_HOURS = 24;

function getDismissStorageKey(memberUuid: string | null): string | null {
  if (!memberUuid) return null;
  return `${DISMISS_STORAGE_KEY_PREFIX}${memberUuid}`;
}

function getDismissedWarnings(memberUuid: string | null): Map<number, number> {
  const key = getDismissStorageKey(memberUuid);
  if (!key) return new Map();
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return new Map();
    const parsed = JSON.parse(stored) as [number, number][];
    const now = Date.now();
    return new Map(parsed.filter(([, expiry]) => expiry > now));
  } catch {
    return new Map();
  }
}

function saveDismissedWarnings(memberUuid: string | null, dismissed: Map<number, number>) {
  const key = getDismissStorageKey(memberUuid);
  if (!key) return;
  localStorage.setItem(key, JSON.stringify([...dismissed]));
}

export function PolicyWarningBanner({ className }: PolicyWarningBannerProps) {
  const memberUuid = useAuthStore(state => state.memberUuid);
  const [dismissed, setDismissed] = useState<Map<number, number>>(
    () => getDismissedWarnings(memberUuid),
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['policy-warnings', memberUuid],
    queryFn: () => accountMonitoringApi.listWarnings(true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: !!memberUuid,
  });

  if (isLoading || isError || !data) {
    return null;
  }

  // Filter out dismissed warnings (that haven't expired)
  const now = Date.now();
  const activeWarnings = data.warnings.filter(w => {
    const expiry = dismissed.get(w.id);
    return !expiry || expiry <= now;
  });

  if (activeWarnings.length === 0) {
    return null;
  }

  const handleDismiss = (warningId: number) => {
    const expiry = Date.now() + (DISMISS_DURATION_HOURS * 60 * 60 * 1000);
    setDismissed(prev => {
      const updated = new Map(prev);
      updated.set(warningId, expiry);
      saveDismissedWarnings(memberUuid, updated);
      return updated;
    });
  };

  return (
    <div className={cn('space-y-2', className)}>
      {activeWarnings.map((warning) => (
        <WarningItem
          key={warning.id}
          warning={warning}
          onDismiss={() => handleDismiss(warning.id)}
        />
      ))}
    </div>
  );
}

interface WarningItemProps {
  warning: PolicyWarning;
  onDismiss: () => void;
}

function WarningItem({ warning, onDismiss }: WarningItemProps) {
  const accountDisplay = warning.account_name
    ? `${warning.account_name} (${formatAccountId(warning.google_ads_account_id)})`
    : formatAccountId(warning.google_ads_account_id);

  const reasonDisplay = warning.warning_reason === 'high_disapproval_rate'
    ? 'a high disapproval rate'
    : 'multiple policy violations';

  // Format policy topics from SCREAMING_SNAKE_CASE to Title Case
  const formatPolicyTopic = (topic: string): string => {
    return topic
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div
      className={cn(
        'relative flex items-start gap-3 px-4 py-3 rounded-lg border',
        'bg-yellow-50 border-yellow-200 text-yellow-800'
      )}
      role="alert"
    >
      {/* Warning icon */}
      <svg
        className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          Policy Warning: {accountDisplay}
        </p>
        <p className="text-sm mt-1">
          This account has {reasonDisplay}.{' '}
          {warning.days_remaining > 0 ? (
            <>
              <span className="font-semibold">{warning.days_remaining} days</span> remaining to resolve
              before automatic unlink.
            </>
          ) : (
            <span className="font-semibold text-red-700">Grace period expired.</span>
          )}
        </p>
        {warning.policy_topics.length > 0 && (
          <p className="text-xs mt-1 text-yellow-700">
            Issues: {warning.policy_topics.slice(0, 3).map(formatPolicyTopic).join(', ')}
            {warning.policy_topics.length > 3 && ` +${warning.policy_topics.length - 3} more`}
          </p>
        )}
      </div>

      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="flex-shrink-0 p-1 rounded hover:bg-yellow-100 transition-colors"
        aria-label="Dismiss warning"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default PolicyWarningBanner;
